import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock } from 'lucide-react';
import { usePaymentCycle, usePaymentDeadline } from '@/hooks/useFinanceAnalytics';
import { cn } from '@/lib/utils';

export default function PaymentCycleCard({ branchId, startDate, endDate }) {
  const { data: cycleData, isLoading: cycleLoading } = usePaymentCycle(branchId, startDate, endDate);
  const { data: deadlineDays, isLoading: deadlineLoading } = usePaymentDeadline(branchId);

  const isLoading = cycleLoading || deadlineLoading;

  // Determine color based on threshold
  const getStatusColor = () => {
    if (!cycleData?.avg_days || !deadlineDays) return 'text-slate-600';

    const ratio = cycleData.avg_days / deadlineDays;

    if (ratio <= 0.7) {
      return 'text-emerald-600'; // Good - green
    } else if (ratio <= 1.0) {
      return 'text-orange-500'; // Warning - orange
    } else {
      return 'text-red-600'; // Bad - red
    }
  };

  const getBgColor = () => {
    if (!cycleData?.avg_days || !deadlineDays) return 'bg-slate-100';

    const ratio = cycleData.avg_days / deadlineDays;

    if (ratio <= 0.7) {
      return 'bg-emerald-50';
    } else if (ratio <= 1.0) {
      return 'bg-orange-50';
    } else {
      return 'bg-red-50';
    }
  };

  const getStatusText = () => {
    if (!cycleData?.avg_days || !deadlineDays) return null;

    const ratio = cycleData.avg_days / deadlineDays;

    if (ratio <= 0.7) {
      return 'Dobry wynik';
    } else if (ratio <= 1.0) {
      return 'Zbliża się do limitu';
    } else {
      return 'Przekracza limit';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-12 w-20" />
          <Skeleton className="h-3 w-24 mt-2" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("transition-colors", getBgColor())}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-2">
          <Clock className={cn("w-4 h-4", getStatusColor())} />
          <p className="text-sm text-slate-500">Średni czas płatności</p>
        </div>

        <div className="flex items-baseline gap-2">
          <p className={cn("text-4xl font-bold", getStatusColor())}>
            {cycleData?.avg_days != null
              ? Math.round(cycleData.avg_days)
              : '—'
            }
          </p>
          <span className="text-lg text-slate-500">dni</span>
        </div>

        <div className="mt-2 space-y-1">
          <p className="text-xs text-slate-400">
            limit: {deadlineDays} dni
          </p>
          {getStatusText() && (
            <p className={cn("text-xs font-medium", getStatusColor())}>
              {getStatusText()}
            </p>
          )}
          {cycleData?.sample_count > 0 && (
            <p className="text-xs text-slate-400">
              na podstawie {cycleData.sample_count} płatności
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
